import * as XLSX from 'xlsx';
import { supabase } from '../supabaseClient';

export class ExcelPayrollProcessor {
  constructor() {
    this.onProgress = null;
    this.onComplete = null;
    this.onError = null;
    this.onPreview = null;
  }

  setProgressCallback(callback) {
    this.onProgress = callback;
  }

  setCompleteCallback(callback) {
    this.onComplete = callback;
  }

  setErrorCallback(callback) {
    this.onError = callback;
  }

  setPreviewCallback(callback) {
    this.onPreview = callback;
  }

  async processExcelFile(file, company) {
    try {
      if (this.onProgress) {
        this.onProgress({ stage: 'reading', message: 'Reading Excel file...' });
      }

      const data = await this.readExcelFile(file);
      
      if (this.onProgress) {
        this.onProgress({ stage: 'processing', message: 'Processing payroll data...' });
      }
      
      const payrollData = this.extractPayrollData(data, company);
      
      if (this.onPreview) {
        this.onPreview(payrollData);
      }

      return payrollData;
    } catch (error) {
      console.error('Excel processing error:', error);
      if (this.onError) {
        this.onError({ error: error.message || 'Failed to process Excel file' });
      }
      throw error;
    }
  }

  readExcelFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          resolve(workbook);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  }

  extractPayrollData(workbook, company) {
    const sheetName = 'Payroll New';
    const sheet = workbook.Sheets[sheetName];
    
    if (!sheet) {
      throw new Error(`Sheet "${sheetName}" not found in Excel file`);
    }

    const rawData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null });
    
    // Find header row and date row
    let headerRowIndex = -1;
    let dateRowIndex = -1;
    
    for (let i = 0; i < Math.min(10, rawData.length); i++) {
      const row = rawData[i];
      if (row && row[0] === 'Name' && row[1] === 'Aname') {
        headerRowIndex = i;
        dateRowIndex = i + 1;
        break;
      }
    }
    
    if (headerRowIndex === -1) {
      throw new Error('Could not find header row in Excel file');
    }

    // Extract dates from the date row
    const dateRow = rawData[dateRowIndex];
    const dates = [];
    
    for (let col = 5; col <= 18; col++) {
      if (dateRow[col]) {
        dates.push(this.parseExcelDate(dateRow[col]));
      }
    }

    // Process staff data
    const payrollRecords = [];
    const attendanceRecords = [];
    const skippedEmployees = [];
    
    for (let i = headerRowIndex + 2; i < rawData.length; i++) {
      const row = rawData[i];
      
      if (!row || !row[0] || typeof row[0] !== 'string') continue;
      
      const name = row[0].trim();
      
      // Skip non-employee rows
      if (this.isNonEmployeeRow(name)) {
        continue;
      }
      
      const aname = row[1] || '';
      const lunchEligibility = row[2] || ''; // Column C: lunch Eligibility (Y/N)
      const rowCompany = row[3] || company;
      const department = row[4] || '';
      
      // Extract hours for two weeks
      const hours = {};
      let totalHours = 0;
      
      for (let week = 1; week <= 2; week++) {
        for (let day = 0; day < 7; day++) {
          const colIndex = 5 + (week - 1) * 7 + day;
          const value = row[colIndex];
          const dayHours = this.parseHours(value);
          
          const dayNames = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
          const key = `week${week}_${dayNames[day]}`;
          hours[key] = dayHours;
          totalHours += dayHours;
          
          // Create attendance record if hours > 0
          if (dayHours > 0 && dates[colIndex - 5]) {
            // Extract store info from the row below the hours (if available)
            const storeInfoRow = i + 1 < rawData.length ? rawData[i + 1] : null;
            let storeCode = rowCompany;
            let storeName = rowCompany === 'KF' ? 'Main Store' : 'Koo Store';
            
            // Try to extract store code from the row below the hours
            if (storeInfoRow && storeInfoRow[colIndex]) {
              const storeInfo = String(storeInfoRow[colIndex]).trim();
              if (storeInfo && storeInfo.length <= 10) { // Store codes are typically short
                storeCode = storeInfo;
                storeName = this.mapStoreCodeToName(storeInfo);
              }
            }
            
            attendanceRecords.push({
              employee_number: this.generateEmployeeNumber(aname, name),
              employee_name: (aname && aname.trim()) ? aname.trim() : this.cleanName(name), // Use Aname for employee name if available
              company: rowCompany,
              department: department,
              date: this.formatDate(dates[colIndex - 5]),
              hours: dayHours,
              store_code: storeCode,
              store_name: storeName,
              day_name: dates[colIndex - 5] ? dates[colIndex - 5].toLocaleDateString('en-US', { weekday: 'long' }) : 'Unknown'
            });
          }
        }
      }
      
      // Skip employees with 0 total hours
      if (totalHours === 0) {
        skippedEmployees.push({
          name: this.cleanName(name),
          reason: 'Zero hours'
        });
        continue;
      }
      
      // Extract additional payroll data from later columns
      // Based on user's Excel screenshot with column numbers: 19=PTO/HP, 20=S/L, 21=Lunch, 22=Total Hrs, etc.
      const ptoHp = this.parseNumericValue(row[19]) || 0; // Column 19 (PTO/HP)
      const sickLeave = this.parseNumericValue(row[20]) || 0; // Column 20 (S/L)
      const lunchHours = this.parseNumericValue(row[21]) || 0; // Column 21 (Lunch hours for subsidy)
      const totalHoursFromSheet = this.parseNumericValue(row[22]) || totalHours; // Column 22 (Total Hrs)
      const checkCapHours = this.parseNumericValue(row[23]) || 0; // Column 23 (Check Cap Hours)
      const checkHours = this.parseNumericValue(row[24]) || 0; // Column 24 (Check Hours)
      const weekdayCashHours = this.parseNumericValue(row[25]) || 0; // Column 25 (Weekday Cash Hours)
      const weekendCashHours = this.parseNumericValue(row[26]) || 0; // Column 26 (Weekend Cash Hours)
      const checkRate = this.parseNumericValue(row[27]) || 0; // Column 27 (Check Rate)
      const weekdayCashRate = this.parseNumericValue(row[28]) || 0; // Column 28 (Weekday cash rate)
      const weekendCashRate = this.parseNumericValue(row[29]) || 0; // Column 29 (Weekend Cash Rate)
      const checkPay = this.parseNumericValue(row[30]) || 0; // Column 30 (Check Pay)
      const weekdayCashPay = this.parseNumericValue(row[31]) || 0; // Column 31 (Weekday cash Pay)
      const weekendCashPay = this.parseNumericValue(row[32]) || 0; // Column 32 (Weekend Cash Pay)
      const totalCashPay = this.parseNumericValue(row[33]) || 0; // Column 33 (Total Cash Pay)
      const totalPay = this.parseNumericValue(row[34]) || 0; // Column 34 (Total Pay)

      // Create payroll verification record
      const payrollRecord = {
        employee_number: this.generateEmployeeNumber(aname, name),
        employee_name: (aname && aname.trim()) ? aname.trim() : this.cleanName(name), // Use Aname for employee name if available
        company: rowCompany,
        department: department,
        ...hours,
        lunch: lunchHours,
        total_hours: totalHoursFromSheet > 0 ? totalHoursFromSheet : totalHours,
        pto_hp: ptoHp,
        sick_leave: sickLeave,
        check_cap_hours: checkCapHours,
        check_hours: checkHours,
        weekday_cash_hours: weekdayCashHours,
        weekend_cash_hours: weekendCashHours,
        check_rate: checkRate,
        weekday_cash_rate: weekdayCashRate,
        weekend_cash_rate: weekendCashRate,
        check_pay: checkPay,
        weekday_cash_pay: weekdayCashPay,
        weekend_cash_pay: weekendCashPay,
        total_cash_pay: totalCashPay,
        total_pay: totalPay
      };
      
      payrollRecords.push(payrollRecord);
    }
    
    return {
      payrollRecords,
      attendanceRecords,
      skippedEmployees,
      summary: {
        totalEmployees: payrollRecords.length,
        totalAttendanceRecords: attendanceRecords.length,
        totalSkipped: skippedEmployees.length,
        periodStart: dates[0] ? this.formatDate(dates[0]) : null,
        periodEnd: dates[dates.length - 1] ? this.formatDate(dates[dates.length - 1]) : null
      }
    };
  }

  parseExcelDate(value) {
    if (value instanceof Date) {
      return value;
    }
    
    if (typeof value === 'number') {
      // Excel date serial number to UTC date
      const utcDate = new Date((value - 25569) * 86400 * 1000);
      // Convert to local date to match Excel display
      const localDate = new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate());
      return localDate;
    }
    
    if (typeof value === 'string') {
      return new Date(value);
    }
    
    return null;
  }

  formatDate(date) {
    if (!date) return null;
    // Ensure we use local date components to avoid timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  parseHours(value) {
    if (!value || value === '') return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const num = parseFloat(value);
      return isNaN(num) ? 0 : num;
    }
    return 0;
  }

  parseNumericValue(value) {
    if (!value || value === '') return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      // Remove currency symbols and commas
      const cleaned = value.replace(/[$,]/g, '');
      const num = parseFloat(cleaned);
      return isNaN(num) ? 0 : num;
    }
    return 0;
  }

  isNonEmployeeRow(name) {
    const nonEmployeePatterns = [
      'Holiday Pay',
      'Vacation Pay',
      'Weekend rate',
      'H = Holiday',
      'V/S = Vacation',
      'Personal Day Pay',
      'Hours:',
      'Gross Pay:',
      'Total',
      'Summary',
      'TOTAL',
      'SUMMARY'
    ];
    
    // Check if it's a pattern we know to skip
    const hasNonEmployeePattern = nonEmployeePatterns.some(pattern => 
      name.toLowerCase().includes(pattern.toLowerCase())
    );
    
    // Check if name is too short (likely not a real name)
    const isTooShort = name.length < 3;
    
    // Check if it looks like a number (employee IDs that are just numbers)
    const isJustNumber = /^\d+$/.test(name.trim());
    
    // Check if it's empty or only whitespace
    const isEmpty = !name || name.trim() === '';
    
    return hasNonEmployeePattern || isTooShort || isJustNumber || isEmpty;
  }

  cleanName(name) {
    if (!name) return '';
    
    // Remove common suffixes
    let cleaned = name.trim();
    
    if (cleaned.includes(' -')) {
      cleaned = cleaned.split(' -')[0].trim();
    }
    
    if (cleaned.includes('(')) {
      cleaned = cleaned.split('(')[0].trim();
    }
    
    return cleaned;
  }

  generateEmployeeNumber(aname, fullName) {
    if (aname && aname.trim()) {
      return aname.toLowerCase().trim();
    }
    return fullName.toLowerCase().replace(/\s+/g, '_');
  }

  mapStoreCodeToName(storeCode) {
    // Map store codes to full store names based on the existing mapping
    const storeMapping = {
      'Main': 'Main St',
      'M': 'Main St',
      'Kissena': 'Kissena Blvd',
      'KS': 'Kissena Blvd',
      'K': 'Kissena Blvd',
      'Roosevelt': 'Roosevelt Ave',
      'R': 'Roosevelt Ave',
      'ROO': 'Roosevelt Ave',
      '39Ave': '39th Ave',
      '39': '39th Ave',
      'KF': 'Main Store',
      'KOO': 'Koo Store'
    };
    
    return storeMapping[storeCode] || storeCode;
  }

  async validateAndMapEmployees(payrollRecords) {
    if (this.onProgress) {
      this.onProgress({ stage: 'validating', message: 'Validating employee records...' });
    }

    // Get all employees from database
    const { data: employees, error } = await supabase
      .from('employees')
      .select('*');

    if (error) {
      throw new Error(`Failed to fetch employees: ${error.message}`);
    }

    const validRecords = [];
    const unmatchedRecords = [];
    
    for (const record of payrollRecords) {
      // Try to find employee by various matching strategies
      const matchingEmployee = this.findMatchingEmployee(record, employees);
      
      if (matchingEmployee) {
        validRecords.push({
          ...record,
          employee_id: matchingEmployee.employee_number,
          matched_employee: matchingEmployee
        });
      } else {
        unmatchedRecords.push(record);
      }
    }

    return { validRecords, unmatchedRecords };
  }

  findMatchingEmployee(record, employees) {
    const recordName = record.employee_name.toLowerCase();
    const recordNumber = record.employee_number.toLowerCase();
    
    // Try exact employee_number match
    let match = employees.find(emp => 
      emp.employee_number && emp.employee_number.toLowerCase() === recordNumber
    );
    
    if (match) return match;
    
    // Try name variations
    for (const emp of employees) {
      if (!emp.payroll_name) continue;
      
      const empName = emp.payroll_name.toLowerCase();
      
      // Exact name match
      if (empName === recordName) return emp;
      
      // Try short_name match
      if (emp.short_name && emp.short_name.toLowerCase() === recordNumber) return emp;
      
      // Try first name from payroll_name
      const empFirstName = empName.split(' ')[0];
      if (empFirstName === recordNumber) return emp;
    }
    
    return null;
  }

  async importToDatabase(payrollRecords, attendanceRecords, company, payrollNumber) {
    if (this.onProgress) {
      this.onProgress({ 
        stage: 'importing', 
        message: `Importing data to database for payroll #${payrollNumber}...`,
        percentage: 0
      });
    }

    try {
      // Add payroll_number to all records
      const payrollRecordsWithNumber = payrollRecords.map(record => ({
        ...record,
        payroll_number: payrollNumber
      }));

      const attendanceRecordsWithNumber = attendanceRecords.map(record => ({
        ...record,
        payroll_number: payrollNumber
      }));

      // Delete existing records for the company and payroll period
      const { error: deletePayrollError } = await supabase
        .from('payroll_summary')
        .delete()
        .eq('company', company)
        .eq('payroll_number', payrollNumber);

      if (deletePayrollError) {
        throw new Error(`Failed to delete existing payroll records: ${deletePayrollError.message}`);
      }

      const { error: deleteAttendanceError } = await supabase
        .from('payroll_attendance')
        .delete()
        .eq('company', company)
        .eq('payroll_number', payrollNumber);

      if (deleteAttendanceError) {
        throw new Error(`Failed to delete existing attendance records: ${deleteAttendanceError.message}`);
      }

      // Insert new records in batches
      const batchSize = 50;
      
      // Insert payroll records
      for (let i = 0; i < payrollRecordsWithNumber.length; i += batchSize) {
        const batch = payrollRecordsWithNumber.slice(i, i + batchSize);
        
        const { error } = await supabase
          .from('payroll_summary')
          .insert(batch);

        if (error) {
          throw new Error(`Failed to insert payroll records: ${error.message}`);
        }

        if (this.onProgress) {
          const percentage = Math.round(((i + batch.length) / payrollRecordsWithNumber.length) * 50);
          this.onProgress({
            stage: 'importing',
            message: `Importing payroll records...`,
            percentage: percentage
          });
        }
      }

      // Insert attendance records
      for (let i = 0; i < attendanceRecordsWithNumber.length; i += batchSize) {
        const batch = attendanceRecordsWithNumber.slice(i, i + batchSize);
        
        const { error } = await supabase
          .from('payroll_attendance')
          .insert(batch);

        if (error) {
          throw new Error(`Failed to insert attendance records: ${error.message}`);
        }

        if (this.onProgress) {
          const percentage = 50 + Math.round(((i + batch.length) / attendanceRecordsWithNumber.length) * 50);
          this.onProgress({
            stage: 'importing',
            message: `Importing attendance records...`,
            percentage: percentage
          });
        }
      }

      if (this.onComplete) {
        this.onComplete({
          success: true,
          message: `Successfully imported ${payrollRecordsWithNumber.length} payroll records and ${attendanceRecordsWithNumber.length} attendance records for payroll #${payrollNumber}`,
          details: {
            payrollCount: payrollRecordsWithNumber.length,
            attendanceCount: attendanceRecordsWithNumber.length,
            payrollNumber: payrollNumber
          }
        });
      }

      return { success: true };
    } catch (error) {
      if (this.onError) {
        this.onError({ error: error.message });
      }
      throw error;
    }
  }
}

export default ExcelPayrollProcessor;